import React, { useState } from "react";
import { toast } from "sonner";
import { Send, ExternalLink, BellRing } from "lucide-react";
import { safeJson } from "@/lib/api";
import { Spinner } from "@/components/Spinner";
import TelegramGuide from "@/components/TelegramGuide";

const Step = ({ n, title, children }) => (
  <div className="flex gap-3.5">
    <div className="w-8 h-8 shrink-0 rounded-full bg-[#0ea5e9]/20 border-[1.5px] border-[#0ea5e9] flex items-center justify-center text-[#0ea5e9] font-bold text-sm mt-0.5">{n}</div>
    <div className="flex-1"><p className="font-bold text-white mb-1">{title}</p>{children}</div>
  </div>
);

export default function TelegramSetup({ apiFetch, onDone }) {
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    const token = botToken.trim();
    const chat = chatId.trim();
    if (!token) { toast.error("Please enter your Bot Token"); return; }
    if (!chat) { toast.error("Please enter your Telegram Chat ID"); return; }
    setSaving(true);
    try {
      const res = await apiFetch("/api/user/profile/api-keys", {
        method: "POST", body: JSON.stringify({ telegram_bot_token: token, telegram_chat_id: chat }),
      });
      const data = await safeJson(res);
      if (data && data.success) { toast.success("Saved — now send a test to confirm"); setSaved(true); }
      else toast.error((data && data.detail) || "Failed to save");
    } catch { toast.error("Network error. Please try again."); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await apiFetch("/api/user/profile/telegram/test", { method: "POST", body: JSON.stringify({}) });
      const data = await safeJson(res);
      if (data && data.success) toast.success("Test alert sent — check your Telegram!");
      else toast.error((data && data.error) || "Could not send test message");
    } catch { toast.error("Network error. Please try again."); }
    finally { setTesting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] overflow-y-auto">
      <div className="max-w-md mx-auto p-6">
        <div className="flex flex-col items-center py-7">
          <div className="rounded-full bg-[#0ea5e9]/10 border-2 border-[#0ea5e9] flex items-center justify-center mb-4" style={{ width: 88, height: 88 }}>
            <Send size={44} color="#0ea5e9" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Your Private Alerts</h1>
          <p className="text-[#9ca3af] text-center text-[15px] leading-6 px-2">Create your OWN Telegram bot so trade executions, bets won and bets lost are sent privately to you — only you receive them.</p>
        </div>

        <div className="space-y-5 mb-6">
          <Step n={1} title="Create Your Bot">
            <p className="text-sm text-[#9ca3af]">Open @BotFather, send <span className="font-mono bg-[#1e293b] px-1 rounded">/newbot</span>, choose a name, then copy the <b>Bot Token</b> it gives you.</p>
            <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 bg-[#0ea5e9]/10 text-[#0ea5e9] text-[13px] font-semibold px-3 py-2 rounded-lg"><ExternalLink size={14} /> Open @BotFather</a>
          </Step>
          <Step n={2} title="Start Your Bot">
            <p className="text-sm text-[#9ca3af]">Open the bot you just created and tap <span className="font-mono bg-[#1e293b] px-1 rounded">Start</span> so it can message you.</p>
          </Step>
          <Step n={3} title="Get Your Chat ID">
            <p className="text-sm text-[#9ca3af]">Open @userinfobot and tap Start — it replies with your numeric ID.</p>
            <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 bg-[#0ea5e9]/10 text-[#0ea5e9] text-[13px] font-semibold px-3 py-2 rounded-lg"><ExternalLink size={14} /> Open @userinfobot</a>
          </Step>
        </div>

        <TelegramGuide />

        <label className="text-sm font-semibold text-[#e2e8f0] mb-2 block">Bot Token</label>
        <input
          value={botToken} onChange={(e) => { setBotToken(e.target.value); setSaved(false); }}
          placeholder="123456789:AAH..." data-testid="telegram-bot-token-input"
          className="w-full bg-[#1e293b] border border-[#334155] rounded-xl p-3.5 text-white outline-none focus:border-[#0ea5e9] mb-4"
        />

        <label className="text-sm font-semibold text-[#e2e8f0] mb-2 block">Telegram Chat ID</label>
        <input
          value={chatId} onChange={(e) => { setChatId(e.target.value); setSaved(false); }} inputMode="numeric"
          placeholder="123456789" data-testid="telegram-id-input"
          className="w-full bg-[#1e293b] border border-[#334155] rounded-xl p-3.5 text-white outline-none focus:border-[#0ea5e9] mb-5"
        />

        {!saved ? (
          <button onClick={save} disabled={saving} data-testid="telegram-save-btn" className="w-full bg-[#0ea5e9] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Spinner size={20} color="#fff" /> : (<><BellRing size={20} /> Enable My Alerts</>)}
          </button>
        ) : (
          <>
            <button onClick={sendTest} disabled={testing} data-testid="telegram-test-btn" className="w-full border border-[#0ea5e9] text-[#0ea5e9] font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 mb-2.5">
              {testing ? <Spinner size={20} color="#0ea5e9" /> : (<><Send size={20} /> Send Test Message</>)}
            </button>
            <button onClick={onDone} data-testid="telegram-continue-btn" className="w-full bg-[#10b981] text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2">
              Continue
            </button>
          </>
        )}
        <button onClick={onDone} data-testid="telegram-skip-btn" className="w-full text-[#6b7280] text-sm py-3 mt-2">I&apos;ll do this later</button>
      </div>
    </div>
  );
}
